import { computed } from "vue";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

/**
 * Whether the PDP's add-to-cart button is unavailable for the current variant
 * selection. One copy of a rule that used to sit in every template's button:
 * a product with variants is judged by the picked variant's own stock, not by
 * the parent's total.
 *
 * The two hooks exist so a template re-skins the button without re-stating the
 * rule — both default to the base behaviour, so passing neither reproduces it
 * exactly.
 */
export const useAddToCartDisabled = (
	stock: () => number,
	options: {
		/** Set for a quick-add from somewhere other than this product's PDP. */
		wishProduct?: () => ProductItem | undefined;
		/**
		 * ANDed into every branch. gotaplatsensfoto gates the whole rule on a
		 * Storyblok stock status, so an out-of-stock product stays buyable
		 * unless that status says otherwise.
		 */
		gate?: () => boolean;
		/**
		 * Result when no variant branch applies — a wishlist quick-add, or a
		 * second parametric picked without a colour. Base leaves the button
		 * enabled and lets the store answer.
		 */
		fallback?: () => boolean;
	} = {}
) => {
	const { selectedColor, selectedSecondParametric } =
		storeToRefs(useProductStore());

	return computed(() => {
		const gate = options.gate ? options.gate() : true;

		if (!options.wishProduct?.()) {
			if (!selectedColor.value && !selectedSecondParametric.value) {
				return stock() < 1 && gate;
			}
			if (selectedColor.value) {
				if (selectedSecondParametric.value) {
					return !selectedSecondParametric.value?.InStock && gate;
				}
				return !selectedColor.value?.InStock && gate;
			}
		}

		return options.fallback ? options.fallback() : false;
	});
};
